import { ApexOptions } from 'apexcharts'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getStoreDetail } from 'services'
import { GetStoreDetailRes } from 'types/storeDetail'

export const useStoreDetail = () => {
  const [searchParams] = useSearchParams()
  const storeID = searchParams.get('store_id')
  const machineID = searchParams.get('machine_id')
  const unitNo = searchParams.get('unit_no')
  const hitDate = searchParams.get('hit_date')
  const calcNum = searchParams.get('calc_num') as string

  const [data, setData] = useState<GetStoreDetailRes['data']>([])

  useEffect(() => {
    ;(async () => {
      if (storeID && machineID && unitNo) {
        try {
          const {
            data: { data },
          } = await getStoreDetail(
            machineID,
            storeID,
            unitNo,
            hitDate || dayjs().format('YYYY-MM-DD'),
            calcNum,
          )
          setData(data)
        } catch (error) {
          console.error(error)
        }
      }
    })()
  }, [calcNum, hitDate, machineID, storeID, unitNo])

  const optionsChart: ApexOptions = useMemo(() => {
    return {
      chart: {
        id: 'areachart-2',
        type: 'line',
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      grid: {
        show: true,
        borderColor: '#FFFFFF',
        position: 'back',
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
        padding: {
          right: 30,
          left: 20,
        },
      },
      fill: {
        type: 'solid',
        opacity: 0,
      },
      stroke: {
        curve: 'straight',
        width: 1.8,
      },
      dataLabels: {
        enabled: false,
      },
      yaxis: {
        max: max => customRound(max) + 5000,
        min: min => customRound(min) - 5000,
        labels: {
          style: {
            colors: '#A3A3A3',
            fontFamily: 'Roboto',
            fontSize: '12px',
            fontWeight: '600',
          },
          offsetX: 0,
          formatter: function (val) {
            return val.toFixed(0)
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      xaxis: {
        type: 'datetime',
        min: new Date('01/01/2014').getTime(),
        max: new Date('01/06/2014').getTime(),
        labels: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      tooltip: {
        enabled: false,
      },
      legend: {
        show: false,
      },
    }
  }, [])

  const series: ApexAxisChartSeries | ApexNonAxisChartSeries = useMemo(() => {
    const dataConverts = data.map(ds => {
      const dataConvert = []
      const startTime = new Date('01/01/2014').getTime()
      const totalTime = 86400000 * 4.5
      const rawData = ds.map(d => d.net) || []

      for (let i = 0; i < rawData.length; i++) {
        const time = startTime + (totalTime / (rawData.length - 1)) * i
        const innerArr = [time, rawData[i]]
        dataConvert.push(innerArr)
      }
      return dataConvert
    })

    return dataConverts.map((d, index) => {
      const getColor = () => {
        switch (index) {
          case 2:
            return '#009999'
          case 1:
            return '#0066FF'
          default:
            return '#CC3030'
        }
      }
      return {
        name: `${index}`,
        color: getColor(),
        data: d,
      }
    })
  }, [data])

  const dataText = useMemo(() => {
    const dataConvert = data.map(ds => ds[ds.length - 1].net - ds[0].net)
    return {
      fist: dataConvert[0],
      second: dataConvert[1],
      third: dataConvert[2],
    }
  }, [data])

  const positionPoint = useMemo(() => {
    const dataConvert = data.map(ds => ds[ds.length - 1].net)
    const maxData = Math.max(...data.flatMap(item => item.map(i => i.net)))
    const minData = Math.min(...data.flatMap(item => item.map(i => i.net)))
    const minDataChart = customRound(minData) - 5000
    const maxDataChart = customRound(maxData) + 5000
    const totalCount = maxDataChart - minDataChart

    return {
      first: dataConvert[0]
        ? ((dataConvert[0] - minDataChart) / totalCount) * 100
        : undefined,
      second: dataConvert[1]
        ? ((dataConvert[1] - minDataChart) / totalCount) * 100
        : undefined,
      third: dataConvert[2]
        ? ((dataConvert[2] - minDataChart) / totalCount) * 100
        : undefined,
    }
  }, [data])

  return {
    optionsChart,
    series,
    dataText,
    positionPoint,
    params: {
      storeID,
      machineID,
      unitNo,
      hitDate,
    },
  }
}

function customRound(num: number) {
  return num > 0 ? Math.ceil(num / 1000) * 1000 : Math.floor(num / 1000) * 1000
}
